import { Article } from '../hooks/useArticles';

interface ArticleListProps {
  articles: Article[];
  onSelectArticle: (article: Article) => void;
  onRefresh?: () => Promise<void>;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export function ArticleList({ articles, onSelectArticle, onRefresh }: ArticleListProps) {
  if (articles.length === 0) {
    return (
      <div className="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        <h3>No articles saved</h3>
        <p>Articles you save will appear here for reading anytime, even offline.</p>
        {onRefresh && (
          <button className="refresh-btn" onClick={onRefresh}>
            Refresh
          </button>
        )}
        <style>{`
          .refresh-btn {
            margin-top: 1rem;
            padding: 0.75rem 1.5rem;
            background: var(--accent, #f97316);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }
          .refresh-btn:hover {
            background: #ea580c;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="article-list">
      {articles.map((article) => (
        <article
          key={article.id}
          className="article-card"
          onClick={() => onSelectArticle(article)}
        >
          <div className="article-card-header">
            <h3 className="article-title">{article.title}</h3>
            {article.isFavorite && (
              <span className="favorite-badge" title="Favorite">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </span>
            )}
          </div>
          <p className="article-excerpt">{article.excerpt}</p>
          <div className="article-meta">
            <span className="reading-time">{article.readingTime} min read</span>
            <span className="saved-time">{formatRelativeTime(article.savedAt)}</span>
            {article.readingProgress > 0 && (
              <span className="progress-badge">
                {Math.round(article.readingProgress)}% read
              </span>
            )}
          </div>
          {article.tags.length > 0 && (
            <div className="article-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          )}
        </article>
      ))}
      <style>{`
        .article-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .favorite-badge {
          color: #ef4444;
          flex-shrink: 0;
        }
        .progress-badge {
          color: var(--accent, #f97316);
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}
